import React, { useState } from 'react'
import { LineChart } from '@mui/x-charts/LineChart'
import { ChartsReferenceLine } from '@mui/x-charts/ChartsReferenceLine'
import { Paper, Box, Typography, Grid, useTheme, useMediaQuery } from '@mui/material'
import { FinancialsGrowthResponseData } from '../Config/types'
import { tableColors, indicatorColors, companyColors } from '../Config/colors'

// Convert RGB array to CSS color string
const rgbToCss = (rgb: number[]): string => `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`

interface GrowthLineChartProps {
  data: FinancialsGrowthResponseData
  dataField: 'mean_growth_rate' | 'growth_rate'
  /** When true, periods are displayed in descending order on the x-axis. */
  invertXAxisSort?: boolean
}

type MetricKey = 'equity' | 'free_cash_flow' | 'revenue' | 'earnings_per_share_diluted'

interface MetricConfig {
  key: MetricKey
  label: string
  shortLabel: string
}

const METRICS: MetricConfig[] = [
  { key: 'equity', label: 'Equity Growth', shortLabel: 'Equity' },
  { key: 'free_cash_flow', label: 'Free Cash Flow Growth', shortLabel: 'FCF' },
  { key: 'revenue', label: 'Revenue Growth', shortLabel: 'Revenue' },
  { key: 'earnings_per_share_diluted', label: 'EPS Growth', shortLabel: 'EPS' },
]

export const GrowthLineChart: React.FC<GrowthLineChartProps> = ({
  data,
  dataField,
  invertXAxisSort = false,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // State to track which companies are visible
  const [visibleCompanies, setVisibleCompanies] = useState<Set<string>>(() => 
    new Set(data?.financials_growths?.map(fg => fg.symbol) || [])
  )

  if (!data || !data.financials_growths || data.financials_growths.length === 0) {
    return null
  }

  // Handler to toggle company visibility
  const toggleCompanyVisibility = (symbol: string) => {
    setVisibleCompanies(prev => {
      const newSet = new Set(prev)
      if (newSet.has(symbol)) {
        newSet.delete(symbol)
      } else {
        newSet.add(symbol)
      }
      return newSet
    })
  }

  // Get all unique periods from the data and sort them
  const allPeriods = new Set<number>()
  data.financials_growths.forEach(fg => {
    fg[dataField].forEach(record => {
      allPeriods.add(record.period)
    })
  })
  const periodsAsc = Array.from(allPeriods).sort((a, b) => a - b)
  const periods = invertXAxisSort ? [...periodsAsc].reverse() : periodsAsc

  // Create series data for each company, filtered by visibility
  const createSeriesForMetric = (metric: MetricKey) => {
    return data.financials_growths
      .filter(fg => visibleCompanies.has(fg.symbol))
      .map((fg, index) => {
        // Find original index for consistent color
        const originalIndex = data.financials_growths.findIndex(f => f.symbol === fg.symbol)
        const dataPoints = periods.map(period => {
          const record = fg[dataField].find(r => r.period === period)
          return record ? record[metric] * 100 : null // Convert to percentage
        })
        
        return {
          data: dataPoints,
          label: fg.symbol,
          color: companyColors[originalIndex % companyColors.length],
          showMark: true,
          valueFormatter: (value: number | null) => 
            value !== null ? `${value.toFixed(2)}%` : 'N/A',
        }
      })
  }

  // Calculate chart height based on viewport to ensure each subplot fits on one screen
  // On mobile, use viewport height accounting for overhead (navbar, title, legend, padding)
  const getChartHeight = () => {
    if (typeof window === 'undefined') return isMobile ? 200 : 250
    const viewportHeight = window.innerHeight
    // Overhead: navbar (64px) + paper padding + title + legend + margins (~200px)
    const overhead = 200
    const availableHeight = viewportHeight - overhead
    
    if (isMobile) {
      // Use ~55% of available viewport height for chart, with min/max bounds
      return Math.min(Math.max(availableHeight * 0.55, 150), 350)
    }
    return 250 // Desktop: fixed height works well
  }
  const chartHeight = getChartHeight()

  const renderMiniChart = (metricConfig: MetricConfig) => {
    const series = createSeriesForMetric(metricConfig.key)
    
    return (
      <Box
        key={metricConfig.key}
        sx={{
          p: 1,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          backgroundColor: 'white',
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
            mb: 1,
            textAlign: 'center',
          }}
        >
          {isMobile ? metricConfig.shortLabel : metricConfig.label}
        </Typography>
        <LineChart
          xAxis={[
            {
              data: periods,
              label: isMobile ? 'Years' : 'Period (Years)',
              scaleType: 'point',
              tickLabelStyle: {
                fontSize: isMobile ? 9 : 11,
              },
              labelStyle: {
                fontSize: isMobile ? 10 : 12,
              },
            },
          ]}
          yAxis={[
            {
              label: isMobile ? '%' : 'Growth Rate (%)',
              tickLabelStyle: {
                fontSize: isMobile ? 9 : 11,
              },
              labelStyle: {
                fontSize: isMobile ? 10 : 12,
              },
            },
          ]}
          series={series}
          height={chartHeight}
          margin={{
            top: 15,
            right: isMobile ? 8 : 20,
            bottom: isMobile ? 30 : 45,
            left: isMobile ? 40 : 55,
          }}
          slotProps={{
            legend: {
              hidden: true,
            } as any, // Hide legend in individual charts, show once at bottom
          }}
          sx={{
            '& .MuiChartsAxis-line': {
              stroke: theme.palette.divider,
            },
            '& .MuiChartsAxis-tick': {
              stroke: theme.palette.divider,
            },
            '& .MuiChartsAxis-tickLabel': {
              fill: theme.palette.text.secondary,
            },
            '& .MuiChartsAxis-label': {
              fill: theme.palette.text.primary,
            },
            '& .MuiLineElement-root': {
              strokeWidth: 2,
            },
            '& .MuiMarkElement-root': {
              scale: isMobile ? '0.8' : '1',
            },
          }}
        >
          <ChartsReferenceLine
            y={0}
            lineStyle={{
              stroke: theme.palette.text.secondary,
              strokeWidth: 1,
              strokeDasharray: '4 2',
            }}
          />
          {/* Green dashed line at 10% growth rate target */}
          <ChartsReferenceLine
            y={10}
            lineStyle={{
              stroke: rgbToCss(indicatorColors.fontColors.green),
              strokeWidth: 2,
              strokeDasharray: '6 4',
            }}
          />
        </LineChart>
      </Box>
    )
  }

  return (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        backgroundColor: 'white',
        borderRadius: 2,
      }}
    >
      <Typography
        variant="h6"
        component="h2"
        gutterBottom
        sx={{
          fontWeight: 600,
          color: theme.palette.primary.main,
          mb: 2,
        }}
      >
        Growth Rate Overview
      </Typography>

      <Grid container spacing={isMobile ? 1 : 2}>
        {METRICS.map((metric) => (
          <Grid size={{ xs: 12, sm: 6 }} key={metric.key}>
            {renderMiniChart(metric)}
          </Grid>
        ))}
      </Grid>

      {/* Interactive Legend */}
      <Box
        sx={{
          mt: 2,
          p: 1.5,
          backgroundColor: tableColors.describingCellBackground,
          borderRadius: 1,
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: isMobile ? 1 : 2,
        }}
      >
        {data.financials_growths.map((fg, index) => {
          const isVisible = visibleCompanies.has(fg.symbol)
          return (
            <Box
              key={fg.symbol}
              onClick={() => toggleCompanyVisibility(fg.symbol)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                opacity: isVisible ? 1 : 0.4,
                transition: 'opacity 0.2s ease',
                '&:hover': {
                  opacity: isVisible ? 0.8 : 0.6,
                },
              }}
            >
              <Box
                sx={{
                  width: 16,
                  height: 3,
                  backgroundColor: companyColors[index % companyColors.length],
                  borderRadius: 1,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 500,
                  color: theme.palette.text.secondary,
                  textDecoration: isVisible ? 'none' : 'line-through',
                }}
              >
                {fg.symbol}
              </Typography>
            </Box>
          )
        })}
        {/* 10% target line legend */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <svg width="20" height="10">
            <line
              x1="0"
              y1="5"
              x2="20"
              y2="5"
              stroke={rgbToCss(indicatorColors.fontColors.green)}
              strokeWidth="2"
              strokeDasharray="4 2"
            />
          </svg>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 500,
              color: theme.palette.text.secondary,
            }}
          >
            10% Target
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mt: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {dataField === 'mean_growth_rate' 
            ? 'Shows average annual growth rates over different time periods.'
            : 'Shows year-over-year growth rates for each period.'}
        </Typography>
      </Box>
    </Paper>
  )
}

export default GrowthLineChart
